import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { confirmDialog } from "../../../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { getTermsSections, saveTermsSections } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminCMSTerms = () => {
  const [sections, setSections] = useState([]);
  const [originalSections, setOriginalSections] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Validation & Change tracking
  const isFormInvalid = useMemo(() => {
    if (sections.length === 0) return false;
    return sections.some((s) => !s.title?.trim() || !s.content?.trim());
  }, [sections]);

  const isChanged = useMemo(() => {
    if (sections.length !== originalSections.length) return true;
    return sections.some((s, i) => {
      const orig = originalSections[i];
      return s.title !== orig.title || s.content !== orig.content;
    });
  }, [sections, originalSections]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stored = await getTermsSections();
        if (!mounted) return;
        const data = (Array.isArray(stored) ? stored : []).map((s) => ({
          ...s,
          titleTouched: true,
          contentTouched: true,
        }));
        setSections(JSON.parse(JSON.stringify(data)));
        setOriginalSections(JSON.parse(JSON.stringify(data)));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load Terms & Conditions",
        );
        setSections([]);
        setOriginalSections([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const addSection = () => {
    const nextId = sections.length
      ? Math.max(...sections.map((s) => s.id)) + 1
      : 1;
    setSections([
      ...sections,
      {
        id: nextId,
        title: "",
        content: "",
        titleTouched: false,
        contentTouched: false,
      },
    ]);
  };

  const updateSection = (index, field, value) => {
    const next = [...sections];
    next[index] = {
      ...next[index],
      [field]: value,
      [`${field}Touched`]: true,
    };
    setSections(next);
  };

  const removeSection = async (index) => {
    const confirmed = await confirmDialog({
      title: "Remove section?",
      text: "This section will be removed from Terms & Conditions.",
    });
    if (!confirmed) return;
    setSections(sections.filter((_, i) => i !== index));
    Swal.fire({
      icon: "success",
      title: "Removed",
      text: "This Section removed successfully.",
      confirmButtonColor: "#38915c",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Sections",
        text: "Please fill in all titles and content before saving.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveTermsSections(sections);
      const data = Array.isArray(saved) ? saved : sections;
      setSections(JSON.parse(JSON.stringify(data)));
      setOriginalSections(JSON.parse(JSON.stringify(data)));
      setError("");
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Terms & Conditions updated successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to save Terms & Conditions",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">
            Terms & Conditions
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage platform terms. All fields are required.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {sections.length === 0 && (
          <div className="p-6 rounded-xl border-2 border-dashed border-gray-200 bg-white text-gray-600 text-center">
            Data is not available
          </div>
        )}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="p-6 rounded-xl border-2 border-gray-200 bg-white"
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className="text-sm font-medium text-gray-500">
                Section {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                title="Remove section"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <div className="grid gap-4">
              <TextInput
                label="Title"
                value={section.title || ""}
                onChange={(e) => updateSection(index, "title", e.target.value)}
                placeholder="Section title"
                error={
                  section.titleTouched &&
                  !section.title?.trim() &&
                  "Title is required"
                }
                required
              />
              <TextInput
                label="Content"
                value={section.content || ""}
                onChange={(e) =>
                  updateSection(index, "content", e.target.value)
                }
                placeholder="Section content"
                error={
                  section.contentTouched &&
                  !section.content?.trim() &&
                  "Content is required"
                }
                multiline
                rows={4}
                required
              />
            </div>
          </div>
        ))}
        <div className="flex gap-4 mt-8 items-stretch">
          <button
            type="button"
            onClick={addSection}
            className="w-[70%] flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
          >
            <Plus size={20} />
            Add section
          </button>
          <div className="w-[30%]">
            <Button
              variant="primary"
              onClick={handleSave}
              className="w-full shadow-md"
              disabled={saving || !isChanged || isFormInvalid}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCMSTerms;
